/**
 * ProviderBadgeCard Component
 * Feature: 009-provider-ui-pixel-perfect
 * Task: T091 - Badge card sub-component
 * 
 * Displays a single bQuest verification badge with icon, name, description, and dates.
 */

import { Award, CheckCircle2, Star, Shield, Users, BadgeCheck } from 'lucide-react';
import type { ProviderBadge } from '../types/provider-profile.types';

type ProviderBadgeCardProps = {
  badge: ProviderBadge;
};

/**
 * Maps icon names to Lucide React components
 */
const iconMap: Record<string, typeof CheckCircle2> = {
  CheckCircle2,
  Star,
  Shield,
  Award,
  Users,
  BadgeCheck,
};

/**
 * Formats date string to readable format (e.g., "March 2012")
 */
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
};

export const ProviderBadgeCard = ({ badge }: ProviderBadgeCardProps) => {
  const { badgeName, iconName, description, earnedDate, expiresAt } = badge;
  
  // Get icon component from map, default to CheckCircle2
  const IconComponent = iconName && iconMap[iconName] ? iconMap[iconName] : CheckCircle2;
  
  const earnedDateFormatted = formatDate(earnedDate);
  const expirationDateFormatted = expiresAt ? formatDate(expiresAt) : null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
      {/* Icon and Badge Name */}
      <div className="flex items-start gap-4 mb-3">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-[#026857]/10 rounded-full flex items-center justify-center">
            <IconComponent 
              className="w-6 h-6 text-[#026857]" 
              data-testid="badge-icon"
            />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {badgeName}
          </h3>
          <p className="text-sm text-gray-600 line-clamp-2">
            {description}
          </p>
        </div>
      </div>

      {/* Dates */}
      <div className="mt-4 pt-4 border-t border-gray-100 text-sm text-gray-500">
        <div className="flex items-center gap-2">
          <span className="font-medium">Earned:</span>
          <span>{earnedDateFormatted}</span>
        </div>
        {expirationDateFormatted && (
          <div className="flex items-center gap-2 mt-1">
            <span className="font-medium">Expires:</span>
            <span>{expirationDateFormatted}</span>
          </div>
        )}
      </div>
    </div>
  );
};

